import { App as CapApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { getPushPermission, syncPushToken } from "../lib/push";

/**
 * Текущее разрешение на пуши. Перечитывается при возврате в приложение
 * (например, из системных настроек). Если пользователь включил пуши
 * в настройках, сразу регистрирует токен — не дожидаясь следующего запуска.
 */
export const usePushPermission = () => {
  const [permission, setPermissionState] = useState<NotificationPermission>();
  const lastRef = useRef<NotificationPermission>();

  const setPermission = useCallback((value: NotificationPermission) => {
    lastRef.current = value;
    setPermissionState(value);
  }, []);

  const refresh = useCallback(async () => {
    let next: NotificationPermission;
    try {
      next = await getPushPermission();
    } catch {
      next = "denied";
    }

    const prev = lastRef.current;
    setPermission(next);

    if (prev && prev !== "granted" && next === "granted") {
      syncPushToken().catch((error) =>
        console.error("[push] token sync failed", error),
      );
    }
  }, [setPermission]);

  useEffect(() => {
    refresh();

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const resumeListener = Capacitor.isNativePlatform()
      ? CapApp.addListener("resume", () => refresh())
      : undefined;

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      resumeListener?.then((handle) => handle.remove());
    };
  }, [refresh]);

  return [permission, setPermission] as const;
};
